/**
 * Forced password change at first login (Annex A 28/05/2026).
 *
 * Reached automatically after a successful dealer login when the
 * backend returns ``must_change_password: true``. Requires both the
 * temporary (current) password and the new one — endpoint validates
 * length ≥ 10 + must differ from temp.
 *
 * The page is rendered in the dealer's preferred UI language
 * (``dealer_lang`` in localStorage, set at first login from the
 * dealer's country). This matches the multilingual welcome email
 * the dealer just received so the entire onboarding feels coherent.
 */
import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { KeyRound, ShieldCheck, AlertCircle } from "lucide-react";
import { PasswordInput } from "../components/shared/PasswordInput";
import { toast } from "sonner";
import axios from "axios";
import { API, LOJACK_LOGO } from "../config";
import { getDealerFormI18n } from "../translations/dealerCustomerForm";

const DealerForcedChangePasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  // Resolve the dealer's UI language (set at login by the language switcher,
  // or pre-seeded by the welcome email link). Falls back to IT for safety.
  const lang = (localStorage.getItem("dealer_lang") || "IT").toUpperCase();
  const t = getDealerFormI18n(lang);

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!token) return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-white mb-1">{t.fpSessionInvalid}</h2>
        <p className="text-gray-500 text-sm mb-4">{t.fpMissingToken}</p>
        <Link to="/dealer" className="text-lojack-red hover:underline text-sm">{t.fpBackToLogin}</Link>
      </div>
    </div>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 10) {
      setError(t.fpPasswordTooShort);
      return;
    }
    if (next !== confirm) {
      setError(t.fpPasswordsMismatch);
      return;
    }
    if (next === current) {
      setError(t.fpSameAsTemp);
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/dealer/change-password?token=${token}`,
        { current_password: current, new_password: next },
      );
      toast.success(t.fpSuccess);
      localStorage.removeItem("dealer_token");
      localStorage.removeItem("dealer_role");
      navigate("/dealer");
    } catch (err) {
      setError(err.response?.data?.detail || t.fpUpdateFailed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full" data-testid="dealer-forced-change-password-page">
        <div className="text-center mb-6">
          <img src={LOJACK_LOGO} alt="LoJack" className="h-10 mx-auto mb-4" />
        </div>
        <div className="bg-[#111] border border-lojack-clause-500/30 rounded-2xl p-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-lojack-clause-500/15 text-lojack-clause-400 flex items-center justify-center shrink-0">
              <KeyRound size={20} />
            </div>
            <div>
              <h1 className="text-white font-bold text-base">{t.fpTitle}</h1>
              <p className="text-gray-500 text-xs mt-0.5">{t.fpSubtitle}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-gray-400 text-xs">{t.fpTempLabel}</label>
              <PasswordInput
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
                required
                data-testid="forced-change-current-password"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs">{t.fpNewLabel}</label>
              <PasswordInput
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
                required
                data-testid="forced-change-new-password"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs">{t.fpConfirmLabel}</label>
              <PasswordInput
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#0a0a0a] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none"
                required
                data-testid="forced-change-confirm-password"
              />
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-3 flex items-start gap-2" data-testid="forced-change-error">
                <AlertCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
                <p className="text-red-300 text-xs">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-lojack w-full flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="forced-change-submit"
            >
              {loading ? t.fpUpdating : <><ShieldCheck size={14} /> {t.fpSetBtn}</>}
            </button>
          </form>

          <p className="text-gray-600 text-[10px] text-center mt-5 leading-relaxed">
            {t.fpSecurityNotice}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DealerForcedChangePasswordPage;
